import { ReactNode } from 'react';
import dayjs from 'dayjs';
import {
  DecomActionDaysModel,
  formatStringList,
  getUserNameChangeExample,
  USER_NAME_CHANGE_DATE_FORMAT,
  USER_NAME_CHANGE_EXAMPLE_EMAIL,
} from '../decom-policy-utils';
import HtmlPreview from '../../../../../shared/html-preview';
import AvatarChip from '../../../../../shared/avatar-chip';
import { gPanel_Tier } from '../../../../../../types';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

type NarrativeParams = Partial<DecomActionDaysModel> & {
  gPanelTier: gPanel_Tier;
};

const highlight = (text: string) => (
  <Box component="span" sx={{ fontWeight: 'bold' }}>
    {text}
  </Box>
);

const simpleSentence = (start: string, value?: string, end = '') => (
  <Box component="span">
    {start}
    {value && highlight(value)}
    {end}
  </Box>
);

const destinationUser = (
  destUser?: string[] | string | null,
  useManagerEmail?: boolean | null
) => {
  if (Array.isArray(destUser)) {
    if (destUser.length === 0) {
      return useManagerEmail ? 'the manager' : '';
    }
    return destUser
      .map((user) => {
        if (typeof user === 'string') return user;
        if (
          user &&
          typeof user === 'object' &&
          'userEmail' in user &&
          typeof (user as { userEmail?: unknown }).userEmail === 'string'
        ) {
          const u = user as { userEmail: string };
          return u.userEmail;
        }
        return '';
      })
      .filter(Boolean)
      .join(', ');
  }
  if (typeof destUser === 'string') {
    return destUser;
  }
  return useManagerEmail ? 'the manager' : '';
};

export const decomNarratives: Record<
  string,
  string | ((params: NarrativeParams) => ReactNode)
> = {
  transferDocs: ({ destUser, useManagerEmail, destFolderName }) => {
    let destinationChip: ReactNode;
    if (destFolderName) {
      // When destFolderName exists, show both user and shared drive
      let userChip: ReactNode;
      if (useManagerEmail) {
        userChip = <AvatarChip type="User" email="Manager" />;
      } else if (typeof destUser === 'string') {
        userChip = <AvatarChip type="User" email={destUser} />;
      } else if (destUser && Array.isArray(destUser) && destUser.length > 0) {
        const firstUser = destUser[0];
        const userEmail =
          typeof firstUser === 'string' ? firstUser : firstUser?.userEmail;
        if (userEmail) {
          userChip = <AvatarChip type="User" email={userEmail} />;
        } else {
          userChip = <Box component="span">a destination user</Box>;
        }
      } else {
        // Fallback if destUser is missing (shouldn't happen in normal flow)
        userChip = <Box component="span">a destination user</Box>;
      }

      const sharedDriveChip = (
        <AvatarChip type="Shared Drive" email={destFolderName} />
      );

      return (
        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
          Drive folders and files will be transferred to &nbsp;
          {userChip} &nbsp; and then to &nbsp; {sharedDriveChip}.
        </Box>
      );
    } else if (useManagerEmail) {
      // Handle useManagerEmail when destUser might be null/undefined
      destinationChip = <AvatarChip type="User" email="Manager" />;
    } else if (destUser) {
      if (typeof destUser === 'string') {
        destinationChip = <AvatarChip type="User" email={destUser} />;
      } else if (Array.isArray(destUser) && destUser.length > 0) {
        const firstUser = destUser[0];
        const userEmail =
          typeof firstUser === 'string' ? firstUser : firstUser?.userEmail;
        if (userEmail) {
          destinationChip = <AvatarChip type="User" email={userEmail} />;
        } else {
          destinationChip = <Box component="span">a destination user</Box>;
        }
      } else {
        // Fallback if destUser is missing or invalid
        destinationChip = <Box component="span">a destination user</Box>;
      }
    }

    return (
      <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
        Drive folders and files will be transferred to &nbsp;
        {destinationChip}.
      </Box>
    );
  },
  transfercals: ({ destUser, useManagerEmail }) =>
    simpleSentence(
      "The decommissioned user's primary calendar will be transferred to ",
      destinationUser(
        Array.isArray(destUser)
          ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
          : destUser,
        useManagerEmail
      ),
      '.'
    ),
  ReassignNicknames: ({ destUser, useManagerEmail }) =>
    simpleSentence(
      'Gmail aliases from the decommissioned user will be transferred to ',
      destinationUser(
        Array.isArray(destUser)
          ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
          : destUser,
        useManagerEmail
      ),
      '.'
    ),
  vaultGmailExport: ({ destUser, useManagerEmail }) => {
    const dest = destinationUser(
      Array.isArray(destUser)
        ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
        : destUser,
      useManagerEmail
    );
    if (dest) {
      return simpleSentence(
        'The Gmail of the decommissioned user will be exported to Google Vault and a report will be sent to ',
        dest,
        '.'
      );
    }
    return simpleSentence(
      'The Gmail of the decommissioned user will be exported to Google Vault.'
    );
  },
  vaultCalendarExport: ({ destUser, useManagerEmail }) => {
    const dest = destinationUser(
      Array.isArray(destUser)
        ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
        : destUser,
      useManagerEmail
    );
    if (dest) {
      return simpleSentence(
        'The Google Calendar of the decommissioned user will be exported to Google Vault and a report will be sent to ',
        dest,
        '.'
      );
    }
    return simpleSentence(
      'The Google Calendar of the decommissioned user will be exported to Google Vault.'
    );
  },
  vaultChatExport: ({ destUser, useManagerEmail }) => {
    const dest = destinationUser(
      Array.isArray(destUser)
        ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
        : destUser,
      useManagerEmail
    );
    if (dest) {
      return simpleSentence(
        'The Google Chat of the decommissioned user will be exported to Google Vault and a report will be sent to ',
        dest,
        '.'
      );
    }
    return simpleSentence(
      'The Google Chat of the decommissioned user will be exported to Google Vault.'
    );
  },
  vaultDriveExport: ({ destUser, useManagerEmail }) => {
    const dest = destinationUser(
      Array.isArray(destUser)
        ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
        : destUser,
      useManagerEmail
    );
    if (dest) {
      return simpleSentence(
        'The Google Drive of the decommissioned user will be exported to Google Vault and a report will be sent to ',
        dest,
        '.'
      );
    }
    return simpleSentence(
      'The Google Drive of the decommissioned user will be exported to Google Vault.'
    );
  },
  vaultGeminiExport: ({ destUser, useManagerEmail }) => {
    const dest = destinationUser(
      Array.isArray(destUser)
        ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
        : destUser,
      useManagerEmail
    );
    if (dest) {
      return simpleSentence(
        'The Gemini information of the decommissioned user will be exported to Google Vault and a report will be sent to ',
        dest,
        '.'
      );
    }
    return simpleSentence(
      'The Gemini information of the decommissioned user will be exported to Google Vault.'
    );
  },
  vaultGroupsExport: ({ destUser, useManagerEmail }) => {
    const dest = destinationUser(
      Array.isArray(destUser)
        ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
        : destUser,
      useManagerEmail
    );
    if (dest) {
      return simpleSentence(
        'The Google Groups of the decommissioned user will be exported to Google Vault and a report will be sent to ',
        dest,
        '.'
      );
    }
    return simpleSentence(
      'The Google Groups of the decommissioned user will be exported to Google Vault.'
    );
  },
  EmailDump: ({
    destUser,
    useManagerEmail,
    // startdate,
    // starttime,
    // enddate,
    // endtime,
    // exportType,
    // query,
    // getDeleted,
    gPanelTier,
  }) => (
    <>
      {gPanelTier === 'enterprise'
        ? simpleSentence(
            'The Gmail of the decommissioned user will be exported and automatically downloaded to ',
            destinationUser(
              Array.isArray(destUser)
                ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
                : destUser,
              useManagerEmail
            ),
            "'s Google Drive once complete."
          )
        : simpleSentence(
            'The Gmail of the decommissioned user will be exported and available for download under "Import / Export > Gmail Export".'
          )}
      {/* {startdate && startdate !== 0 ? (
        <Typography variant="body2">
          {highlight('Start Date:')} {new Date(startdate).toLocaleDateString()}
        </Typography>
      ) : null}
      {starttime ? (
        <Typography variant="body2">
          {highlight('Start Time:')} {starttime}
        </Typography>
      ) : null}
      {enddate && enddate !== 0 ? (
        <Typography variant="body2">
          {highlight('End Date:')} {new Date(enddate).toLocaleDateString()}
        </Typography>
      ) : null}
      {endtime ? (
        <Typography variant="body2">
          {highlight('End Time:')} {endtime}
        </Typography>
      ) : null}
      {query ? (
        <Typography variant="body2">
          {highlight('Search Query:')} {query}
        </Typography>
      ) : null}
      {exportType ? (
        <Typography variant="body2">
          {highlight('Export Type:')}{' '}
          {exportType === 'FULL' ? 'Full Message' : 'Headers Only'}
        </Typography>
      ) : null}
      {getDeleted ? (
        <Typography variant="body2">
          {highlight('Get Deleted Messages:')} {getDeleted ? 'Yes' : 'No'}
        </Typography>
      ) : null} */}
    </>
  ),
  setForwarding: ({ destUser, useManagerEmail }) =>
    simpleSentence(
      "A Gmail forward for the decommissioned user's incoming mail will be created to ",
      destinationUser(
        Array.isArray(destUser)
          ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
          : destUser,
        useManagerEmail
      ),
      '.'
    ),
  addDelegate: ({ destUser, useManagerEmail, daysUntilExpiration }) => (
    <>
      {highlight(
        useManagerEmail
          ? 'The manager'
          : Array.isArray(destUser)
          ? destUser[0].userEmail
          : ' '
      )}{' '}
      will be added as a Gmail delegate for the decommissioned user.{' '}
      {daysUntilExpiration && (
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          The delegation will automatically expire after {daysUntilExpiration}{' '}
          {''}
          {daysUntilExpiration === 1 ? 'day' : 'days'}.
        </Box>
      )}
    </>
  ),
  delegateToRelation: ({ destUser, daysUntilExpiration }) => (
    <>
      {highlight(Array.isArray(destUser) ? destUser[0].userEmail : ' ')} will be
      added as a Gmail delegate for the decommissioned user.{' '}
      {daysUntilExpiration && (
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          The delegation will automatically expire after {daysUntilExpiration}{' '}
          {''}
          {daysUntilExpiration === 1 ? 'day' : 'days'}.
        </Box>
      )}
    </>
  ),
  removeEmailAliases: () =>
    'Gmail aliases will be removed from the decommissioned user.',
  removeForwardingAddresses: () =>
    'Gmail forwarding addresses will be removed from the decommissioned user.',
  vacationResponder: ({ subject, message }) => (
    <>
      A vacation responder message will be set for the decommissioned user.
      {subject ? (
        <Typography variant="body2">Subject: {subject}</Typography>
      ) : null}
      {message ? (
        <HtmlPreview
          title="Vacation Responder Preview"
          html={message || ''}
          width={556}
        />
      ) : null}
    </>
  ),
  sendEmail: ({
    emailSendTo,
    emailCcTo,
    emailBccTo,
    useManagerEmail,
    targetEmailType,
    customEmailType,
    emailSubject,
    emailContent,
  }) => {
    const emailTypeDisplay = [
      ...(targetEmailType?.filter((t) => t !== 'custom') || []).map(
        (t) => `${t} email`
      ),
      ...(customEmailType || []).map((t) => `${t} email`),
    ].join(', ');

    const recipients = [
      useManagerEmail ? 'the manager' : '',
      emailTypeDisplay,
      emailSendTo && emailSendTo.length > 0
        ? formatStringList(emailSendTo)
        : '',
    ]
      .filter(Boolean)
      .join(' and ');
    return (
      <>
        An email will be sent to {highlight(recipients)}.
        {emailCcTo && emailCcTo.length > 0 && (
          <>
            {' '}
            CC email will be sent to {highlight(formatStringList(emailCcTo))}.
          </>
        )}
        {emailBccTo && emailBccTo.length > 0 && (
          <>
            {' '}
            BCC email will be sent to {highlight(formatStringList(emailBccTo))}.
          </>
        )}
        {emailSubject ? (
          <Typography variant="body2">
            Subject: {emailSubject.replace(/<p>(.*?)<\/p>/, '$1')}
          </Typography>
        ) : null}
        {emailContent ? (
          <HtmlPreview
            title="Email Preview"
            html={emailContent || ''}
            width={556}
          />
        ) : null}
      </>
    );
  },
  UserNameChange: ({ preName, postName, addDate, datePosition }) => (
    <>
      A prefix{preName ? <> "{highlight(preName)}"</> : null} or suffix
      {postName ? <> "{highlight(postName)}"</> : null} will be added to the
      decommissioned user's primary email address.
      {addDate ? (
        <>
          {' '}
          The decommission date ({USER_NAME_CHANGE_DATE_FORMAT}) will be added
          to the {highlight(datePosition === 'suffix' ? 'suffix' : 'prefix')}.
        </>
      ) : null}
      {preName || postName || addDate ? (
        <>
          {' '}
          Example: {USER_NAME_CHANGE_EXAMPLE_EMAIL} becomes{' '}
          {highlight(
            getUserNameChangeExample(
              { preName, postName, addDate, datePosition },
              dayjs().format(USER_NAME_CHANGE_DATE_FORMAT)
            )
          )}
          .
        </>
      ) : null}
    </>
  ),
  ResetPassword: ({ forceResetPassword, isSendPassword, staticPassword }) => (
    <>
      The decommissioned user's password will be reset to a{' '}
      {staticPassword
        ? highlight('custom password')
        : highlight('randomized password')}
      {isSendPassword
        ? ' and the new password will be sent to the notification user of the policy'
        : ''}
      .
      {forceResetPassword
        ? highlight(' A password reset will be forced on next login')
        : ''}
      .
    </>
  ),
  UserOrgChange: ({ newOrgPath }) =>
    simpleSentence(
      "The decommissioned user's organizational unit will be changed to ",
      destinationUser(newOrgPath),
      '.'
    ),
  RemoveFromAllGroups: () =>
    'The decommissioned user will be removed from all groups.',
  removeSlackChannels: () =>
    'The decommissioned user will be kicked from all Slack channels.',
  RevokeThirdPartyApps: () =>
    'Third-party app access will be removed for the decommissioned user.',
  signOutUser: () =>
    'The decommissioned user will be signed out of all sessions and their sign-in cookies will be reset.',
  removeAllLicenses: () =>
    'Google licenses will be removed from the decommissioned user and a default license will be assigned if applicable.',
  deleteASPS: () =>
    'Application-specific passwords will be deleted from the decommissioned user.',
  deleteUser: ({ mustAcknowledgeLogs }) => (
    <>
      The decommissioned user will be deleted.
      {mustAcknowledgeLogs
        ? highlight(
            ' Acknowledging the logs will be required before the user deletion.'
          )
        : ''}
    </>
  ),
  suspendUser: () => 'The decommissioned user will be suspended.',
  unsuspendUser: () => 'The decommissioned user will be unsuspended.',
  archiveUser: () => 'The decommissioned user will be archived.',
  disableTwoFactor: () =>
    'Two-factor authentication will be disabled for the decommissioned user.',
  hideFromDirectory: () =>
    'The decommissioned user will be hidden from the global address list.',
  ClearPrimaryCalendar: () =>
    "Events will be cleared from the decommissioned user's primary calendar.",
  releaseResourceCalendar: () =>
    'All recurring and non-recurring events will be removed from the calendars of decommissioned resources.',
  docsSharedExternallyReport: ({ destUser, useManagerEmail }) =>
    simpleSentence(
      "A report of the decommissioned user's externally shared drive folders and files will be generated and sent to ",
      destinationUser(
        Array.isArray(destUser)
          ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
          : destUser,
        useManagerEmail
      ),
      '.'
    ),
  actSharedExternallyReportMyDrive: ({ destUser, useManagerEmail }) =>
    simpleSentence(
      "A report of the decommissioned user's external share activities in My Drive will be generated and sent to ",
      destinationUser(
        Array.isArray(destUser)
          ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
          : destUser,
        useManagerEmail
      ),
      '.'
    ),
  actSharedExternallyReportSharedDrive: ({ destUser, useManagerEmail }) =>
    simpleSentence(
      "A report of the decommissioned user's external share activities in Shared Drives will be generated and sent to ",
      destinationUser(
        Array.isArray(destUser)
          ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
          : destUser,
        useManagerEmail
      ),
      '.'
    ),
  actSharedPubliclyReportMyDrive: ({ destUser, useManagerEmail }) =>
    simpleSentence(
      "A report of the decommissioned user's publicly shared content in My Drive will be generated and sent to ",
      destinationUser(
        Array.isArray(destUser)
          ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
          : destUser,
        useManagerEmail
      ),
      '.'
    ),
  actSharedPubliclyReportSharedDrives: ({ destUser, useManagerEmail }) =>
    simpleSentence(
      'A report of publicly shared content in Shared Drives will be generated and sent to ',
      destinationUser(
        Array.isArray(destUser)
          ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
          : destUser,
        useManagerEmail
      ),
      '.'
    ),
  removeExternalShares: ({ destUser, useManagerEmail }) =>
    simpleSentence(
      "External shares from the decommissioned user's Drive will be removed and a report will be sent to ",
      destinationUser(
        Array.isArray(destUser)
          ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
          : destUser,
        useManagerEmail
      ),
      '.'
    ),
  RemoveFromAllSharedDrive: ({ destUser, useManagerEmail }) =>
    simpleSentence(
      'The decommissioned user will be removed from all Shared Drives and a report will be sent to ',
      destinationUser(
        Array.isArray(destUser)
          ? destUser.map((u) => (typeof u === 'string' ? u : u.userEmail))
          : destUser,
        useManagerEmail
      ),
      '.'
    ),
  wipeMobileDevices: () =>
    'Mobile devices of the decommissioned user will be wiped.',
  wipeMobileAccounts: () =>
    'Mobile accounts of the decommissioned user will be wiped.',
  blockMobileDevices: () =>
    'Mobile devices of the decommissioned user will be blocked.',
  deleteMobileDevices: () =>
    'Mobile devices of the decommissioned user will be deleted.',
  removeUserFromShares: () =>
    'Remove the decommissioned user from all the shared files and folders',
  removeUsersShares: ({ sharingScope }) =>
    sharingScope === 'external'
      ? 'External shares on files owned by the decommissioned user will be removed.'
      : sharingScope === 'internal'
      ? 'Internal shares on files owned by the decommissioned user will be removed.'
      : 'All shares on files owned by the decommissioned user will be removed.',
};
